import {
  Alert,
  AlertDescription,
  AlertIcon,
  AlertTitle,
  Box,
  Button,
  Center,
  Flex,
  Heading,
  HStack,
  IconButton,
  Link,
  Stack,
  Table,
  Tag,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  useColorModeValue,
} from '@chakra-ui/react';
import {
  ShiftReportRecord,
  ShiftReportsResponse,
  ShiftStatus,
} from '@bofrak-backend/shared';
import { formatDate } from '@bofrak-backend/shared-i18n';
import { useEffect, useMemo, useState } from 'react';
import { FiChevronLeft, FiChevronRight, FiDownload } from 'react-icons/fi';
import { useQuery } from 'react-query';
import { Loader } from '../../ui';
import { apiAdapter } from '../api/backend';
import { useSharedUiTranslation } from '../../i18n';
import {
  getReportTriggerLabel,
  getShiftStatusLabel,
} from '../utils/status-labels';

const REPORT_FETCH_LIMIT = 100;
const REPORTS_PER_PAGE = 10;

const formatDateTime = (value?: string | number | Date | null) => {
  if (!value) return '—';
  const d = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(d.getTime())) return '—';
  return formatDate(d, 'PPpp');
};

const formatFileSize = (value?: number | null) => {
  if (!value || Number.isNaN(value)) return '—';
  const units = ['B', 'KB', 'MB', 'GB'];
  let size = value;
  let i = 0;
  while (size >= 1024 && i < units.length - 1) {
    size /= 1024;
    i += 1;
  }
  return `${size.toFixed(size < 10 && i > 0 ? 1 : 0)} ${units[i]}`;
};

const STATUS_COLORS: Partial<Record<ShiftStatus, string>> = {
  [ShiftStatus.CLOSED]: 'green',
  [ShiftStatus.CLOSED_WITH_VARIANCE]: 'orange',
};

const getReportTimestamp = (report: ShiftReportRecord) => {
  const requestedAt = new Date(report.requested_at).getTime();
  if (!Number.isNaN(requestedAt)) {
    return requestedAt;
  }

  const lastModified = report.last_modified
    ? new Date(report.last_modified).getTime()
    : Number.NaN;

  return Number.isNaN(lastModified) ? 0 : lastModified;
};

const fetchAllShiftReports = async (
  merchantId: string,
  storeId: string,
): Promise<ShiftReportsResponse> => {
  const reportsByKey = new Map<string, ShiftReportRecord>();
  const visitedCursors = new Set<string>();
  let cursor: string | undefined;

  do {
    const page = await apiAdapter.listShiftReports({
      merchant_id: merchantId,
      store_id: storeId,
      cursor,
      limit: REPORT_FETCH_LIMIT,
    });

    page.reports.forEach((report) => reportsByKey.set(report.key, report));

    const nextCursor = page.next_cursor;
    if (!nextCursor || visitedCursors.has(nextCursor)) {
      break;
    }

    visitedCursors.add(nextCursor);
    cursor = nextCursor;
  } while (cursor);

  return {
    reports: Array.from(reportsByKey.values()).sort(
      (a, b) =>
        getReportTimestamp(b) - getReportTimestamp(a) ||
        b.key.localeCompare(a.key),
    ),
  };
};

interface Props {
  merchantId?: string;
  storeId?: string;
}

const ShiftReportsTab = ({ merchantId, storeId }: Props) => {
  const { t } = useSharedUiTranslation();
  const [currentPage, setCurrentPage] = useState(1);
  const tableHeaderBg = useColorModeValue('gray.50', 'gray.800');
  const tableBorderColor = useColorModeValue('gray.200', 'gray.700');
  const rowHoverBg = useColorModeValue('gray.50', 'gray.900');

  const enabled = Boolean(merchantId && storeId);

  const { data, error, isError, isLoading } = useQuery<
    ShiftReportsResponse,
    Error
  >(
    ['shift-reports', merchantId, storeId],
    () => fetchAllShiftReports(merchantId!, storeId!),
    { enabled, refetchOnWindowFocus: false },
  );

  const reports = data?.reports ?? [];
  const totalPages = Math.max(1, Math.ceil(reports.length / REPORTS_PER_PAGE));
  const activePage = Math.min(currentPage, totalPages);
  const firstReportIndex = (activePage - 1) * REPORTS_PER_PAGE;
  const visibleReports = useMemo(
    () => reports.slice(firstReportIndex, firstReportIndex + REPORTS_PER_PAGE),
    [reports, firstReportIndex],
  );

  useEffect(() => {
    setCurrentPage(1);
  }, [merchantId, storeId]);

  useEffect(() => {
    setCurrentPage((page) => Math.min(page, totalPages));
  }, [totalPages]);

  if (!enabled) {
    return (
      <Alert status="info" borderRadius="md">
        <AlertIcon />
        <Stack spacing={1}>
          <AlertTitle fontSize="sm">
            {t('Select a store to continue')}
          </AlertTitle>
          <AlertDescription fontSize="sm">
            {t('Choose a store from the navigation bar to view shift reports.')}
          </AlertDescription>
        </Stack>
      </Alert>
    );
  }

  if (isLoading) {
    return (
      <Center py={12}>
        <Loader message={t('Loading shift reports...')} />
      </Center>
    );
  }

  if (isError) {
    return (
      <Alert status="error" borderRadius="md">
        <AlertIcon />
        <Stack spacing={1}>
          <AlertTitle fontSize="sm">
            {t('Unable to load shift reports')}
          </AlertTitle>
          <AlertDescription fontSize="sm">
            {error?.message ?? t('Unexpected error.')}
          </AlertDescription>
        </Stack>
      </Alert>
    );
  }

  if (reports.length === 0) {
    return (
      <Stack spacing={3} align="center" py={10} color="gray.600">
        <Heading size="sm">{t('No shift reports yet')}</Heading>
        <Text fontSize="sm" textAlign="center">
          {t(
            'PDF Shift Reports appear here once a shift is closed and a report has been generated (manually or automatically).',
          )}
        </Text>
      </Stack>
    );
  }

  return (
    <Stack spacing={3}>
      <Box
        borderWidth="1px"
        borderRadius="md"
        overflowX="auto"
        borderColor={tableBorderColor}>
        <Table variant="simple" size="md">
          <Thead bg={tableHeaderBg}>
            <Tr>
              <Th>{t('File')}</Th>
              <Th>{t('Shift / Employee')}</Th>
              <Th>{t('Closed at')}</Th>
              <Th>{t('Generated')}</Th>
              <Th>{t('Trigger')}</Th>
              <Th>{t('Size')}</Th>
              <Th textAlign="right">{t('Action')}</Th>
            </Tr>
          </Thead>
          <Tbody>
            {visibleReports.map((report: ShiftReportRecord) => (
              <Tr key={report.key} _hover={{ bg: rowHoverBg }}>
                <Td>
                  <Text fontWeight="medium" wordBreak="break-all">
                    {report.file_name}
                  </Text>
                </Td>
                <Td>
                  <Stack spacing={1}>
                    <Text>{report.employee_name || '—'}</Text>
                    <Tag
                      size="sm"
                      colorScheme={STATUS_COLORS[report.shift_status] ?? 'red'}>
                      {report.shift_status
                        ? getShiftStatusLabel(report.shift_status)
                        : t('Unknown')}
                    </Tag>
                  </Stack>
                </Td>
                <Td>{formatDateTime(report.closed_at)}</Td>
                <Td>{formatDateTime(report.requested_at)}</Td>
                <Td>
                  <Tag
                    size="sm"
                    colorScheme={
                      report.triggered_by === 'manual' ? 'blue' : 'purple'
                    }>
                    {getReportTriggerLabel(report.triggered_by)}
                  </Tag>
                </Td>
                <Td>{formatFileSize(report.size_in_bytes)}</Td>
                <Td textAlign="right">
                  <Button
                    as={Link}
                    href={report.download_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    leftIcon={<FiDownload />}
                    colorScheme="blue"
                    size="sm">
                    {t('Download')}
                  </Button>
                </Td>
              </Tr>
            ))}
          </Tbody>
        </Table>
      </Box>

      <Flex
        direction={{ base: 'column', sm: 'row' }}
        align={{ base: 'stretch', sm: 'center' }}
        justify="space-between"
        gap={3}>
        <Text fontSize="sm" color="gray.600">
          {t('Showing {{from}}-{{to}} of {{total}}', {
            from: firstReportIndex + 1,
            to: Math.min(firstReportIndex + REPORTS_PER_PAGE, reports.length),
            total: reports.length,
          })}
        </Text>
        <HStack spacing={2} justify="flex-end">
          <IconButton
            aria-label={t('Previous page')}
            icon={<FiChevronLeft />}
            onClick={() => setCurrentPage((page) => Math.max(1, page - 1))}
            isDisabled={activePage === 1}
            size="sm"
            variant="outline"
          />
          <Text
            minW="96px"
            textAlign="center"
            fontSize="sm"
            fontWeight="medium">
            {t('Page {{page}} of {{total}}', {
              page: activePage,
              total: totalPages,
            })}
          </Text>
          <IconButton
            aria-label={t('Next page')}
            icon={<FiChevronRight />}
            onClick={() =>
              setCurrentPage((page) => Math.min(totalPages, page + 1))
            }
            isDisabled={activePage === totalPages}
            size="sm"
            variant="outline"
          />
        </HStack>
      </Flex>
    </Stack>
  );
};

export default ShiftReportsTab;
